/** Canonical FAQ copy for /faq and landing teaser (first N items). */
export const faqItems: readonly { question: string; answer: string }[] = [
  {
    question: 'What is PricingTable AI?',
    answer:
      'PricingTable AI helps SaaS teams run pricing experiments on live pricing pages: you define candidate prices and table variations, embed a small integration, and measure how visitors respond so you can improve conversion, revenue, or other goals.',
  },
  {
    question: 'How does pricing A/B testing work on this platform?',
    answer:
      'The product serves variations of your pricing table (often with different price combinations) to visitors. Interactions are recorded so you can compare performance across variations and iterations over time.',
  },
  {
    question: 'Do I need to redesign my pricing page to use it?',
    answer:
      'No. The intent is to work with existing pricing markup and styling. You integrate using generated install guidance and keep your current layout and brand.',
  },
  {
    question: 'What do you mean by "AI" in PricingTable AI?',
    answer:
      'The AI assists with tasks like finding prices in your table and preparing dynamic placeholders so you can generate many testable combinations without hand-editing every cell.',
  },
  {
    question: 'How accurate is automatic price detection?',
    answer:
      "It works well on typical SaaS pricing tables but isn't perfect. You should review detected prices and placeholders before going live, especially for unusual layouts, currency formatting, or non-standard components.",
  },
  {
    question: 'Which platforms and frameworks do you support?',
    answer:
      'The marketing site references 12+ stacks (e.g. React, Next.js, WordPress, PHP, Django, Flask, .NET, Angular, Rails, jQuery, Bootstrap, vanilla JS). Exact coverage is shown in your Install flow when you pick a platform.',
  },
  {
    question: 'How do I install the pricing table on my site?',
    answer:
      'After you configure a table in the app, you copy platform-specific install code (snippet) that loads your experiment and associates traffic with your account. Follow the install page steps for your stack.',
  },
  {
    question: 'What is the API key for?',
    answer:
      'Your API key authenticates the embedded widget / client requests to your PricingTable AI backend so only your account’s configuration and analytics are used.',
  },
  {
    question: 'Where do I find my API key?',
    answer:
      'It is available in the authenticated app (e.g. Install area). The UI is designed so a key is available when you need to integrate—check the install screen if you don’t see it elsewhere.',
  },
  {
    question: 'Can I rotate or regenerate my API key?',
    answer:
      'The current install UX states that keys are created for your account and, in the UI copy, cannot be regenerated—plan integrations and secret storage accordingly. If your deployment needs rotation, that may require a product or support process change.',
  },
  {
    question: 'What metrics and analytics do you provide?',
    answer:
      'Typical signals include clicks, engagement on tiers/plans, conversions where instrumented, and session-level behavior so you can compare variations—not just pageviews.',
  },
  {
    question: 'Can I block internal or bot traffic from analytics?',
    answer:
      'Yes—there is IP blocking functionality so specified addresses don’t pollute experiment or optimization data.',
  },
  {
    question: 'What can I optimize for?',
    answer:
      'Teams often optimize for revenue, conversion rate, or plan mix (e.g. annual vs monthly), depending on strategy. The exact objective labels depend on your in-app optimization settings.',
  },
  {
    question: 'How long should I run a pricing experiment?',
    answer:
      'Long enough for stable traffic and to reduce noise from day-of-week effects. Practical guidance: run until you have enough volume for meaningful comparison; there is no fixed universal duration.',
  },
  {
    question: 'Do you guarantee a revenue lift or statistical significance?',
    answer:
      'No. Legal and product copy note that outcomes aren’t guaranteed; experiments depend on traffic, seasonality, product quality, and how changes are implemented.',
  },
  {
    question: 'What plans are available?',
    answer:
      'As shown on the pricing page: Basic ($29), Pro ($99), and Enterprise ($299), with differences such as table limits, analytics depth, support, and integrations. Dollar amounts may be updated when billing is finalized.',
  },
  {
    question: 'What’s the difference between Basic and Pro?',
    answer:
      'Basic includes a limited number of pricing tables and standard analytics; Pro adds unlimited tables, advanced analytics, priority support, and richer variation and insight features—see the live pricing cards for the canonical list.',
  },
  {
    question: 'What does Enterprise include?',
    answer:
      'Enterprise adds items like dedicated support, white-label options, API access, custom onboarding, and SLA language on the pricing page—confirm with sales if you need custom contracts.',
  },
  {
    question: 'Who should use this product?',
    answer:
      'Product, growth, and pricing stakeholders who own the public pricing page, plus engineering for a one-time embed. After setup, many teams can run day-to-day experiments with light engineering involvement.',
  },
  {
    question: 'Is my customer data safe?',
    answer:
      'Treat all visitor and session data according to your Privacy Policy and Terms (linked from the site footer). Use HTTPS, minimize sensitive fields in experiments, and follow your jurisdiction’s requirements (e.g. GDPR/CCPA) where applicable.',
  },
  {
    question: 'What data do you collect during experiments?',
    answer:
      'Generally: interaction and session data tied to how visitors use the pricing table and which variation they saw—aligned with how experiments and analytics are described in your legal pages. Read the Privacy Policy for the authoritative list.',
  },
  {
    question: 'Can I use this if I’m not a developer?',
    answer:
      'You can configure experiments in the app, but going live usually needs someone who can add the embed snippet to your site or CMS. Non-technical users often pair with a developer for the first install.',
  },
  {
    question: 'What happens if I change my pricing table HTML?',
    answer:
      'You may need to re-upload or re-sync configuration and re-verify that price placeholders still map correctly. Major markup changes should be followed by a quick QA pass in preview or staging.',
  },
  {
    question: 'How do I get help?',
    answer:
      'Use Support (and any in-app channels you expose). Enterprise customers may have priority or dedicated support per plan.',
  },
  {
    question: 'Can I export or use my experiment results elsewhere?',
    answer:
      'Depending on plan and features, you may use analytics in-app or API or export paths where implemented (Enterprise mentions API access on the pricing page). Confirm in-product for what’s enabled today.',
  },
];
